import { useEffect, useState, useMemo } from "react";
import {
  Box,
  Heading,
  Input,
  VStack,
  Text,
  Button,
  Flex,
  RadioGroup,
  Radio,
  Spinner,
  Badge,
  Container,
  Icon,
  InputGroup,
  InputLeftElement,
  Stack,
  Image,
  Divider,
  HStack,
  SimpleGrid,
} from "@chakra-ui/react";
import { SearchIcon, InfoIcon, TimeIcon } from "@chakra-ui/icons";
import {
  IoFilterSharp,
  IoImageOutline,
  IoNewspaperOutline,
  IoPersonOutline,
} from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import ApiResponse from "../../services/ApiResponse";
import { Noticia } from "../../services/models/Noticia";
import UnauthorizedPage from "../UnauthorizedPage";
import ServerErrorPage from "../ServerErrorPage";
import config from "../../MapConfig";

const VISIBILIDAD_OPTIONS = ["Todas", "PUBLICA", "INSTITUCIONAL"];

const NoticiasPage = () => {
  const navigate = useNavigate();
  const [response, setResponse] = useState(new ApiResponse<Noticia[]>());
  const [selectedVisibilidad, setSelectedVisibilidad] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const glassCard = {
    bg: "rgba(255, 255, 255, 0.05)",
    backdropFilter: "blur(12px)",
    border: "1px solid",
    borderColor: "whiteAlpha.200",
    borderRadius: "2xl",
    transition: "all 0.3s ease",
  };

  useEffect(() => {
    const fetchNoticias = async () => {
      const apiResponse = new ApiResponse<Noticia[]>();
      await apiResponse.useFetch("noticias", "GET");
      setResponse(apiResponse);
    };
    fetchNoticias();
  }, []);

  const filteredNoticias = useMemo(() => {
    if (!response.data) return [];

    const normalizedSearch = searchTerm
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();

    return response.data
      .filter((n) => {
        const matchesSearch =
          n.titulo.toLowerCase().includes(normalizedSearch) ||
          n.resumen?.toLowerCase().includes(normalizedSearch);

        const matchesVisibilidad =
          selectedVisibilidad === "" || n.visibilidad === selectedVisibilidad;

        return matchesSearch && matchesVisibilidad && n.estado === 1;
      })
      .sort(
        (a, b) =>
          new Date(b.fecha_publicacion || "").getTime() -
          new Date(a.fecha_publicacion || "").getTime()
      );
  }, [searchTerm, selectedVisibilidad, response.data]);

  if (response.loading)
    return (
      <Flex justify="center" align="center" h="100vh" bg="blue.900">
        <Spinner size="xl" color="#E9681F" thickness="4px" />
      </Flex>
    );

  if (response.responseCode === 401) return <UnauthorizedPage />;
  if (response.responseCode !== 200 && !response.loading)
    return <ServerErrorPage />;

  return (
    <Box bgGradient="linear(to-b, blue.800, #0a192f)" minH="100vh" pb={20}>
      <Container maxW="container.xl" pt={10}>
        {/* Header */}
        <VStack spacing={2} textAlign="center" mb={12} color="white">
          <Badge bg="#E9681F" color="white" px={4} py={1} borderRadius="full">
            {filteredNoticias.length} Noticias
          </Badge>
          <Heading size="2xl" fontWeight="black">
            Noticias & Actualidad
          </Heading>
          <Text fontSize="xl" opacity={0.7}>
            Enterate de las últimas novedades institucionales
          </Text>
        </VStack>

        {/* Buscador */}

        <Flex direction={{ base: "column", lg: "row" }} gap={8}>
          {/* Listado */}
          <Box flex="1">
            <SimpleGrid columns={1} spacing={4}>
              {filteredNoticias.length > 0 ? (
                filteredNoticias.map((noticia) => (
                  <NoticiaCard
                    key={noticia.id}
                    noticia={noticia}
                    onClick={() => navigate(`/noticias/${noticia.id}`)}
                  />
                ))
              ) : (
                <Flex {...glassCard} p={20} justify="center">
                  <Text color="whiteAlpha.500">
                    No hay noticias para mostrar
                  </Text>
                </Flex>
              )}
            </SimpleGrid>
          </Box>
        </Flex>
      </Container>
    </Box>
  );
};

/* Card */
const NoticiaCard = ({
  noticia,
  onClick,
}: {
  noticia: Noticia;
  onClick: () => void;
}) => {
  return (
    <Box
      onClick={onClick}
      cursor="pointer"
      bg="rgba(255, 255, 255, 0.03)"
      border="1px solid"
      borderColor="whiteAlpha.100"
      p={6}
      borderRadius="2xl"
      transition="all 0.3s"
      _hover={{
        bg: "rgba(255, 255, 255, 0.08)",
        transform: "translateX(10px)",
        borderColor: "#E9681F",
      }}
    >
      <Flex gap={6}>
        {noticia.imagen && (
          <Box minW="120px" display="flex" alignItems="center">
            <Image
              src={`${config.API_IMAGES_URL}${
                noticia.imagen.startsWith("/")
                  ? noticia.imagen
                  : `/${noticia.imagen}`
              }`}
              alt={noticia.titulo}
              h="80px"
              w="120px"
              objectFit="cover"
              borderRadius="12px"
              fallback={
                <Icon as={IoImageOutline} boxSize={8} color="gray.300" />
              }
            />
          </Box>
        )}

        <VStack align="start" spacing={2} flex="1">
          <HStack>
            <Badge bg="#E9681F" color="white">
              {noticia.visibilidad}
            </Badge>
            <Badge variant="outline" color="whiteAlpha.700">
              {noticia.tema_nombre}
            </Badge>
          </HStack>

          <Heading size="md" color="white">
            {noticia.titulo}
          </Heading>

          <Text color="whiteAlpha.700" noOfLines={2}>
            {noticia.resumen}
          </Text>

          <HStack fontSize="xs" color="whiteAlpha.500">
            <TimeIcon />
            <Text>{noticia.fecha_publicacion?.split("T")[0]}</Text>
            <Icon as={IoPersonOutline} />
            <Text>{noticia.autor_nombre}</Text>
          </HStack>
        </VStack>

        <Button
          bg="#E9681F"
          color="white"
          _hover={{ bg: "#c85617" }}
          rightIcon={<InfoIcon />}
          borderRadius="xl"
        >
          Leer
        </Button>
      </Flex>
    </Box>
  );
};

export default NoticiasPage;
